class m {
  /**
   * Create a `FluentType` instance.
   *
   * @param value The JavaScript value to wrap.
   */
  constructor(t) {
    this.value = t;
  }
  /**
   * Unwrap the raw value stored by this `FluentType`.
   */
  valueOf() {
    return this.value;
  }
}
class l extends m {
  /**
   * Create an instance of `FluentNone` with an optional fallback value.
   * @param value The fallback value of this `FluentNone`.
   */
  constructor(t = "???") {
    super(t);
  }
  /**
   * Format this `FluentNone` to the fallback string.
   */
  toString(t) {
    return `{${this.value}}`;
  }
}
class h extends m {
  /**
   * Create an instance of `FluentNumber` with options to the
   * `Intl.NumberFormat` constructor.
   *
   * @param value The number value of this `FluentNumber`.
   * @param opts Options which will be passed to `Intl.NumberFormat`.
   */
  constructor(t, e = {}) {
    super(t), this.opts = e;
  }
  /**
   * Format this `FluentNumber` to a string.
   */
  toString(t) {
    try {
      return t.memoizeIntlObject(Intl.NumberFormat, this.opts).format(this.value);
    } catch (e) {
      return t.reportError(e), this.value.toString(10);
    }
  }
}
class E extends m {
  static supportsValue(t) {
    if (typeof t == "number" || t instanceof Date)
      return !0;
    if (t instanceof m)
      return E.supportsValue(t.valueOf());
    if ("Temporal" in globalThis) {
      const e = globalThis.Temporal;
      if (t instanceof e.Instant || t instanceof e.PlainDateTime || t instanceof e.PlainDate || t instanceof e.PlainMonthDay || t instanceof e.PlainTime || t instanceof e.PlainYearMonth)
        return !0;
    }
    return !1;
  }
  /**
   * Create an instance of `FluentDateTime` with options to the
   * `Intl.DateTimeFormat` constructor.
   *
   * @param value The number value of this `FluentDateTime`, in milliseconds.
   * @param opts Options which will be passed to `Intl.DateTimeFormat`.
   */
  constructor(t, e = {}) {
    t instanceof E ? (e = { ...t.opts, ...e }, t = t.value) : t instanceof m && (t = t.valueOf()), typeof t == "object" && "calendarId" in t && e.calendar === void 0 && (e = { ...e, calendar: t.calendarId }), super(t), this.opts = e;
  }
  /**
   * Convert this `FluentDateTime` to a number.
   * Note that this isn't always possible due to the nature of Temporal objects.
   * In such cases, a TypeError will be thrown.
   */
  toNumber() {
    const t = this.value;
    if (typeof t == "number")
      return t;
    if (t instanceof Date)
      return t.getTime();
    if ("epochMilliseconds" in t)
      return t.epochMilliseconds;
    if ("toZonedDateTime" in t)
      return t.toZonedDateTime("UTC").epochMilliseconds;
    throw new TypeError("Unwrapping a non-number value as a number");
  }
  /**
   * Format this `FluentDateTime` to a string.
   */
  toString(t) {
    try {
      return t.memoizeIntlObject(Intl.DateTimeFormat, this.opts).format(this.value);
    } catch (e) {
      return t.reportError(e), typeof this.value == "number" || this.value instanceof Date ? new Date(this.value).toISOString() : this.value.toString();
    }
  }
}
const L = 100, st = "⁨", ot = "⁩";
function at(n, t, e) {
  if (e === t || e instanceof h && t instanceof h && e.value === t.value)
    return !0;
  if (t instanceof h && typeof e == "string") {
    let r = n.memoizeIntlObject(Intl.PluralRules, t.opts).select(t.value);
    if (e === r)
      return !0;
  }
  return !1;
}
function U(n, t, e) {
  return t[e] ? g(n, t[e].value) : (n.reportError(new RangeError("No default")), new l());
}
function $(n, t) {
  const e = [], r = /* @__PURE__ */ Object.create(null);
  for (const s of t)
    s.type === "narg" ? r[s.name] = b(n, s.value) : e.push(b(n, s));
  return { positional: e, named: r };
}
function b(n, t) {
  switch (t.type) {
    case "str":
      return t.value;
    case "num":
      return new h(t.value, {
        minimumFractionDigits: t.precision
      });
    case "var":
      return ut(n, t);
    case "mesg":
      return lt(n, t);
    case "term":
      return ft(n, t);
    case "func":
      return ct(n, t);
    case "select":
      return dt(n, t);
    default:
      return new l();
  }
}
function ut(n, { name: t }) {
  let e;
  if (n.params)
    if (Object.prototype.hasOwnProperty.call(n.params, t))
      e = n.params[t];
    else
      return new l(`$${t}`);
  else if (n.args && Object.prototype.hasOwnProperty.call(n.args, t))
    e = n.args[t];
  else
    return n.reportError(new ReferenceError(`Unknown variable: $${t}`)), new l(`$${t}`);
  if (e instanceof m)
    return e;
  switch (typeof e) {
    case "string":
      return e;
    case "number":
      return new h(e);
    case "object":
      if (E.supportsValue(e))
        return new E(e);
    // eslint-disable-next-line no-fallthrough
    default:
      return n.reportError(new TypeError(`Variable type not supported: $${t}, ${typeof e}`)), new l(`$${t}`);
  }
}
function lt(n, { name: t, attr: e }) {
  const r = n.bundle._messages.get(t);
  if (!r)
    return n.reportError(new ReferenceError(`Unknown message: ${t}`)), new l(t);
  if (e) {
    const s = r.attributes[e];
    return s ? g(n, s) : (n.reportError(new ReferenceError(`Unknown attribute: ${e}`)), new l(`${t}.${e}`));
  }
  return r.value ? g(n, r.value) : (n.reportError(new ReferenceError(`No value: ${t}`)), new l(t));
}
function ft(n, { name: t, attr: e, args: r }) {
  const s = `-${t}`, o = n.bundle._terms.get(s);
  if (!o)
    return n.reportError(new ReferenceError(`Unknown term: ${s}`)), new l(s);
  if (e) {
    const c = o.attributes[e];
    if (c) {
      n.params = $(n, r).named;
      const w = g(n, c);
      return n.params = null, w;
    }
    return n.reportError(new ReferenceError(`Unknown attribute: ${e}`)), new l(`${s}.${e}`);
  }
  n.params = $(n, r).named;
  const f = g(n, o.value);
  return n.params = null, f;
}
function ct(n, { name: t, args: e }) {
  let r = n.bundle._functions[t];
  if (!r)
    return n.reportError(new ReferenceError(`Unknown function: ${t}()`)), new l(`${t}()`);
  if (typeof r != "function")
    return n.reportError(new TypeError(`Function ${t}() is not callable`)), new l(`${t}()`);
  try {
    let s = $(n, e);
    return r(s.positional, s.named);
  } catch (s) {
    return n.reportError(s), new l(`${t}()`);
  }
}
function dt(n, { selector: t, variants: e, star: r }) {
  let s = b(n, t);
  if (s instanceof l)
    return U(n, e, r);
  for (const o of e) {
    const f = b(n, o.key);
    if (at(n, s, f))
      return g(n, o.value);
  }
  return U(n, e, r);
}
function Y(n, t) {
  if (n.dirty.has(t))
    return n.reportError(new RangeError("Cyclic reference")), new l();
  n.dirty.add(t);
  const e = [], r = n.bundle._useIsolating && t.length > 1;
  for (const s of t) {
    if (typeof s == "string") {
      e.push(n.bundle._transform(s));
      continue;
    }
    if (n.placeables++, n.placeables > L)
      throw n.dirty.delete(t), new RangeError(`Too many placeables expanded: ${n.placeables}, max allowed is ${L}`);
    r && e.push(st), e.push(b(n, s).toString(n)), r && e.push(ot);
  }
  return n.dirty.delete(t), e.join("");
}
function g(n, t) {
  return typeof t == "string" ? n.bundle._transform(t) : Y(n, t);
}
class ht {
  constructor(t, e, r) {
    this.dirty = /* @__PURE__ */ new WeakSet(), this.params = null, this.placeables = 0, this.bundle = t, this.errors = e, this.args = r;
  }
  reportError(t) {
    if (!this.errors || !(t instanceof Error))
      throw t;
    this.errors.push(t);
  }
  memoizeIntlObject(t, e) {
    let r = this.bundle._intls.get(t);
    r || (r = {}, this.bundle._intls.set(t, r));
    let s = JSON.stringify(e);
    return r[s] || (r[s] = new t(this.bundle.locales, e)), r[s];
  }
}
function A(n, t) {
  const e = /* @__PURE__ */ Object.create(null);
  for (const [r, s] of Object.entries(n))
    t.includes(r) && (e[r] = s.valueOf());
  return e;
}
const C = [
  "unitDisplay",
  "currencyDisplay",
  "useGrouping",
  "minimumIntegerDigits",
  "minimumFractionDigits",
  "maximumFractionDigits",
  "minimumSignificantDigits",
  "maximumSignificantDigits"
];
function Et(n, t) {
  let e = n[0];
  if (e instanceof l)
    return new l(`NUMBER(${e.valueOf()})`);
  if (e instanceof h)
    return new h(e.valueOf(), {
      ...e.opts,
      ...A(t, C)
    });
  if (e instanceof E)
    return new h(e.toNumber(), {
      ...A(t, C)
    });
  throw new TypeError("Invalid argument to NUMBER");
}
const pt = [
  "dateStyle",
  "timeStyle",
  "fractionalSecondDigits",
  "dayPeriod",
  "hour12",
  "weekday",
  "era",
  "year",
  "month",
  "day",
  "hour",
  "minute",
  "second",
  "timeZoneName"
];
function wt(n, t) {
  let e = n[0];
  if (e instanceof l)
    return new l(`DATETIME(${e.valueOf()})`);
  if (e instanceof E || e instanceof h)
    return new E(e, A(t, pt));
  throw new TypeError("Invalid argument to DATETIME");
}
const K = /* @__PURE__ */ new Map();
function mt(n) {
  const t = Array.isArray(n) ? n.join(" ") : n;
  let e = K.get(t);
  return e === void 0 && (e = /* @__PURE__ */ new Map(), K.set(t, e)), e;
}
class gt {
  /**
   * Create an instance of `FluentBundle`.
   *
   * @example
   * ```js
   * let bundle = new FluentBundle(["en-US", "en"]);
   *
   * let bundle = new FluentBundle(locales, {useIsolating: false});
   *
   * let bundle = new FluentBundle(locales, {
   *   useIsolating: true,
   *   functions: {
   *     NODE_ENV: () => process.env.NODE_ENV
   *   }
   * });
   * ```
   *
   * @param locales - Used to instantiate `Intl` formatters used by translations.
   * @param options - Optional configuration for the bundle.
   */
  constructor(t, { functions: e, useIsolating: r = !0, transform: s = (o) => o } = {}) {
    this._terms = /* @__PURE__ */ new Map(), this._messages = /* @__PURE__ */ new Map(), this.locales = Array.isArray(t) ? t : [t], this._functions = {
      NUMBER: Et,
      DATETIME: wt,
      ...e
    }, this._useIsolating = r, this._transform = s, this._intls = mt(t);
  }
  /**
   * Check if a message is present in the bundle.
   *
   * @param id - The identifier of the message to check.
   */
  hasMessage(t) {
    return this._messages.has(t);
  }
  /**
   * Return a raw unformatted message object from the bundle.
   *
   * Raw messages are `{value, attributes}` shapes containing translation units
   * called `Patterns`. `Patterns` are implementation-specific; they should be
   * treated as black boxes and formatted with `FluentBundle.formatPattern`.
   *
   * @param id - The identifier of the message to check.
   */
  getMessage(t) {
    return this._messages.get(t);
  }
  /**
   * Add a translation resource to the bundle.
   *
   * @example
   * ```js
   * let res = new FluentResource("foo = Foo");
   * bundle.addResource(res);
   * bundle.getMessage("foo");
   * // → {value: .., attributes: {..}}
   * ```
   *
   * @param res
   * @param options
   */
  addResource(t, { allowOverrides: e = !1 } = {}) {
    const r = [];
    for (let s = 0; s < t.body.length; s++) {
      let o = t.body[s];
      if (o.id.startsWith("-")) {
        if (e === !1 && this._terms.has(o.id)) {
          r.push(new Error(`Attempt to override an existing term: "${o.id}"`));
          continue;
        }
        this._terms.set(o.id, o);
      } else {
        if (e === !1 && this._messages.has(o.id)) {
          r.push(new Error(`Attempt to override an existing message: "${o.id}"`));
          continue;
        }
        this._messages.set(o.id, o);
      }
    }
    return r;
  }
  /**
   * Format a `Pattern` to a string.
   *
   * Format a raw `Pattern` into a string. `args` will be used to resolve
   * references to variables passed as arguments to the translation.
   *
   * In case of errors `formatPattern` will try to salvage as much of the
   * translation as possible and will still return a string. For performance
   * reasons, the encountered errors are not returned but instead are appended
   * to the `errors` array passed as the third argument.
   *
   * If `errors` is omitted, the first encountered error will be thrown.
   *
   * @example
   * ```js
   * let errors = [];
   * bundle.addResource(
   *     new FluentResource("hello = Hello, {$name}!"));
   *
   * let hello = bundle.getMessage("hello");
   * if (hello.value) {
   *     bundle.formatPattern(hello.value, {name: "Jane"}, errors);
   *     // Returns "Hello, Jane!" and `errors` is empty.
   *
   *     bundle.formatPattern(hello.value, undefined, errors);
   *     // Returns "Hello, {$name}!" and `errors` is now:
   *     // [<ReferenceError: Unknown variable: name>]
   * }
   * ```
   */
  formatPattern(t, e = null, r = null) {
    if (typeof t == "string")
      return this._transform(t);
    let s = new ht(this, r, e);
    try {
      return Y(s, t).toString(s);
    } catch (o) {
      if (s.errors && o instanceof Error)
        return s.errors.push(o), new l().toString(s);
      throw o;
    }
  }
}
const I = /^(-?[a-zA-Z][\w-]*) *= */gm, j = /\.([a-zA-Z][\w-]*) *= */y, yt = /\*?\[/y, x = /(-?[0-9]+(?:\.([0-9]+))?)/y, bt = /([a-zA-Z][\w-]*)/y, z = /([$-])?([a-zA-Z][\w-]*)(?:\.([a-zA-Z][\w-]*))?/y, vt = /^[A-Z][A-Z0-9_-]*$/, v = /([^{}\n\r]+)/y, Rt = /([^\\"\n\r]*)/y, k = /\\([\\"])/y, V = /\\u([a-fA-F0-9]{4})|\\U([a-fA-F0-9]{6})/y, _t = /^\n+/, Z = / +$/, St = / *\r?\n/g, It = /( *)$/, xt = /{\s*/y, G = /\s*}/y, Tt = /\[\s*/y, $t = /\s*] */y, At = /\s*\(\s*/y, Ot = /\s*->\s*/y, Nt = /\s*:\s*/y, Dt = /\s*,?\s*/y, Mt = /\s+/y;
class Ft {
  constructor(t) {
    this.body = [], I.lastIndex = 0;
    let e = 0;
    for (; ; ) {
      let i = I.exec(t);
      if (i === null)
        break;
      e = I.lastIndex;
      try {
        this.body.push(w(i[1]));
      } catch (a) {
        if (a instanceof SyntaxError)
          continue;
        throw a;
      }
    }
    function r(i) {
      return i.lastIndex = e, i.test(t);
    }
    function s(i, a) {
      if (t[e] === i)
        return e++, !0;
      if (a)
        throw new a(`Expected ${i}`);
      return !1;
    }
    function o(i, a) {
      if (r(i))
        return e = i.lastIndex, !0;
      if (a)
        throw new a(`Expected ${i.toString()}`);
      return !1;
    }
    function f(i) {
      i.lastIndex = e;
      let a = i.exec(t);
      if (a === null)
        throw new SyntaxError(`Expected ${i.toString()}`);
      return e = i.lastIndex, a;
    }
    function c(i) {
      return f(i)[1];
    }
    function w(i) {
      let a = R(), u = H();
      if (a === null && Object.keys(u).length === 0)
        throw new SyntaxError("Expected message value or attributes");
      return { id: i, value: a, attributes: u };
    }
    function H() {
      let i = /* @__PURE__ */ Object.create(null);
      for (; r(j); ) {
        let a = c(j), u = R();
        if (u === null)
          throw new SyntaxError("Expected attribute value");
        i[a] = u;
      }
      return i;
    }
    function R() {
      let i;
      if (r(v) && (i = c(v)), t[e] === "{" || t[e] === "}")
        return _(i ? [i] : [], 1 / 0);
      let a = B();
      return a ? i ? _([i, a], a.length) : (a.value = S(a.value, _t), _([a], a.length)) : i ? S(i, Z) : null;
    }
    function _(i = [], a) {
      for (; ; ) {
        if (r(v)) {
          i.push(c(v));
          continue;
        }
        if (t[e] === "{") {
          i.push(N());
          continue;
        }
        if (t[e] === "}")
          throw new SyntaxError("Unbalanced closing brace");
        let d = B();
        if (d) {
          i.push(d), a = Math.min(a, d.length);
          continue;
        }
        break;
      }
      let u = i.length - 1, p = i[u];
      typeof p == "string" && (i[u] = S(p, Z));
      let y = [];
      for (let d of i)
        d instanceof W && (d = d.value.slice(0, d.value.length - a)), d && y.push(d);
      return y;
    }
    function N() {
      o(xt, SyntaxError);
      let i = D();
      if (o(G))
        return i;
      if (o(Ot)) {
        let a = et();
        return o(G, SyntaxError), {
          type: "select",
          selector: i,
          ...a
        };
      }
      throw new SyntaxError("Unclosed placeable");
    }
    function D() {
      if (t[e] === "{")
        return N();
      if (r(z)) {
        let [, i, a, u = null] = f(z);
        if (i === "$")
          return { type: "var", name: a };
        if (o(At)) {
          let p = Q();
          if (i === "-")
            return { type: "term", name: a, attr: u, args: p };
          if (vt.test(a))
            return { type: "func", name: a, args: p };
          throw new SyntaxError("Function names must be all upper-case");
        }
        return i === "-" ? {
          type: "term",
          name: a,
          attr: u,
          args: []
        } : { type: "mesg", name: a, attr: u };
      }
      return M();
    }
    function Q() {
      let i = [];
      for (; ; ) {
        switch (t[e]) {
          case ")":
            return e++, i;
          case void 0:
            throw new SyntaxError("Unclosed argument list");
        }
        i.push(tt()), o(Dt);
      }
    }
    function tt() {
      let i = D();
      return i.type !== "mesg" ? i : o(Nt) ? {
        type: "narg",
        name: i.name,
        value: M()
      } : i;
    }
    function et() {
      let i = [], a = 0, u;
      for (; r(yt); ) {
        s("*") && (u = a);
        let p = nt(), y = R();
        if (y === null)
          throw new SyntaxError("Expected variant value");
        i[a++] = { key: p, value: y };
      }
      if (a === 0)
        return null;
      if (u === void 0)
        throw new SyntaxError("Expected default variant");
      return { variants: i, star: u };
    }
    function nt() {
      o(Tt, SyntaxError);
      let i;
      return r(x) ? i = F() : i = {
        type: "str",
        value: c(bt)
      }, o($t, SyntaxError), i;
    }
    function M() {
      if (r(x))
        return F();
      if (t[e] === '"')
        return rt();
      throw new SyntaxError("Invalid expression");
    }
    function F() {
      let [, i, a = ""] = f(x), u = a.length;
      return {
        type: "num",
        value: parseFloat(i),
        precision: u
      };
    }
    function rt() {
      s('"', SyntaxError);
      let i = "";
      for (; ; ) {
        if (i += c(Rt), t[e] === "\\") {
          i += it();
          continue;
        }
        if (s('"'))
          return { type: "str", value: i };
        throw new SyntaxError("Unclosed string literal");
      }
    }
    function it() {
      if (r(k))
        return c(k);
      if (r(V)) {
        let [, i, a] = f(V), u = parseInt(i || a, 16);
        return u <= 55295 || 57344 <= u ? (
          // It's a Unicode scalar value.
          String.fromCodePoint(u)
        ) : (
          // Lonely surrogates can cause trouble when the parsing result is
          // saved using UTF-8. Use U+FFFD REPLACEMENT CHARACTER instead.
          "�"
        );
      }
      throw new SyntaxError("Unknown escape sequence");
    }
    function B() {
      let i = e;
      switch (o(Mt), t[e]) {
        case ".":
        case "[":
        case "*":
        case "}":
        case void 0:
          return !1;
        case "{":
          return P(t.slice(i, e));
      }
      return t[e - 1] === " " ? P(t.slice(i, e)) : !1;
    }
    function S(i, a) {
      return i.replace(a, "");
    }
    function P(i) {
      let a = i.replace(St, `
`), u = It.exec(i)[1].length;
      return new W(a, u);
    }
  }
}
class W {
  constructor(t, e) {
    this.value = t, this.length = e;
  }
}
function T(n, t) {
  if (typeof n != "string" || typeof t != "string")
    throw new Error("called addValue with a non-string key or value");
  if (t && t.indexOf(`
`) > -1) {
    const e = `
  `, r = t.split(`
`).join(e);
    return `${n} =${e}${r}`;
  } else
    return `${n} = ${t}`;
}
function Bt(n) {
  if (typeof n != "string")
    throw new Error("called addComment with non-string value");
  return `# ${n.split(`
`).join(`
# `)}
`;
}
function Pt(n, t) {
  const e = Object.entries(n).map(([r, s]) => {
    const o = [];
    if (typeof s == "string")
      return `${T(r, s)}

`;
    if (s.comment && o.push(Bt(s.comment)), typeof s.value != "string")
      throw new Error(`Value ${r} with attributes had no value. Found: ${s.value}`);
    return o.push(T(r, s.value)), Object.entries(s).forEach(([f, c]) => {
      if (!(f === "comment" || f === "value")) {
        if (typeof c != "string")
          throw new Error(`Inner value ${f} with attributes had no value. Found: ${c}`);
        o.push(T(`
  .` + f, c));
      }
    }), o.push(`

`), o.join("");
  }).join("");
  return t && t(null, e), e;
}
function Lt(n, t, e, r) {
  n[t] == null && (n[t] = {}), n[t][e] = r;
}
function Ut(n, t, e) {
  if (n !== void 0 && n[t] !== void 0 && n[t][e] !== void 0)
    return n[t][e];
}
function Ct(n, t, e) {
  if (n !== void 0 && n[t] !== void 0 && n[t][e] !== void 0)
    return n[t][e];
}
function Kt(n) {
  return !/^\s*$/.test(n);
}
function jt(n) {
  return (n.match(/^\s*/) || []).length;
}
function zt(n) {
  const t = n.split(`
`).filter(Kt), e = t.map(jt), r = Math.min(...e), s = new RegExp(`^\\s{${r}}`);
  return t.map((o) => o.replace(s, "")).join(`
`);
}
class q {
  constructor(t, e) {
    this.i18next = e, this.options = t, this.bundles = {};
  }
  createBundle(t, e, r) {
    const s = r !== void 0 ? Pt(r) : "", o = new gt(t, this.options.fluentBundleOptions), f = o.addResource(new Ft(zt(s)));
    if (f.length != 0)
      throw new Error(`Failed to add FluentResource to bundle without errors: ${f.join(`
`)}`);
    Lt(this.bundles, t, e, o);
  }
  createBundleFromI18next(t, e) {
    var r, s;
    if (((r = this.i18next) == null ? void 0 : r.store.data) !== void 0) {
      const o = Ct((s = this.i18next) == null ? void 0 : s.store.data, t, e);
      o !== void 0 && this.createBundle(t, e, o);
    }
  }
  getBundle(t, e) {
    return Ut(this.bundles, t, e);
  }
  bind() {
    this.i18next && (this.i18next.store.on("added", (t, e) => {
      var r;
      (r = this.i18next) != null && r.isInitialized && this.createBundleFromI18next(t, e);
    }), this.i18next.on("initialized", () => {
      if (this.i18next) {
        const t = this.i18next.languages || [], e = this.i18next.options.preload || [], r = this.i18next.options.ns;
        t.filter((s) => !e.includes(s)).concat(e).forEach((s) => {
          typeof r != "string" && (r == null || r.forEach((o) => {
            this.createBundleFromI18next(s, o);
          }));
        });
      }
    }));
  }
}
function X() {
  return {
    bindI18nStore: !0,
    fluentBundleOptions: { useIsolating: !1 }
  };
}
const O = class O {
  constructor(t) {
    this.type = "i18nFormat", this.handleAsObject = !1, this.options = { ...t, ...X() }, this.store = new q(this.options);
  }
  init(t, e) {
    var s;
    const r = ((s = t == null ? void 0 : t.options) == null ? void 0 : s.i18nFormat) || {};
    this.options = { ...r, ...e, ...this.options, ...X() }, this.store = new q(this.options, t), this.options.bindI18nStore && this.store.bind(), t.fluent = this;
  }
  parse(t, e, r, s, o) {
    const f = this.store.getBundle(r, s), c = o.indexOf(".") > -1;
    if (!t) return o;
    const w = c ? t.attributes[o.split(".")[1]] : t.value;
    return !f || !w ? o : f.formatPattern(w, e);
  }
  getResource(t, e, r) {
    const s = this.store.getBundle(t, e), o = r.indexOf(".") > -1 ? r.split(".")[0] : r;
    if (s)
      return s.getMessage(o);
  }
  addLookupKeys(t) {
    return t;
  }
};
O.type = "i18nFormat";
let J = O;
export {
  J as Fluent,
  J as default
};
//# sourceMappingURL=main.js.map
